import { useState, useEffect } from 'react';
import { addNewPeriodical, fetchPeriodicalSites } from '../utils/api';
import CustomSelect from '../components/shared/CustomSelect';
import HelpButton from '../components/shared/HelpButton';
import ContractViewer from '../components/ContractViewer';
import AddNewSiteModal from './AddNewSiteModal';
import { useUser } from '../utils/UserContext';
import { cssUrl } from '../utils/cssVersion';
import { rolesAccess } from '../data/map';

const CYCLE_OPTIONS = [
  'Once-Off', 'Monthly', 'Call-Out', 'Please Delete', '6-Monthly',
  'Quarterly', 'Emergency-Call', 'Yearly', 'Value-Added', 'Weekly',
  'Fortnight', 'Bi-Monthly'
];

const WORK_TYPE_OPTIONS = ['TFN', 'ABN'];
const WORK_STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed'];

// ===== QANTAS — planes-driven quote =====
const QANTAS_SITE = 'Qantas Jet Base';
const PLANES_OPTIONS = ['0', '1', '2', '3'];
const PLANES_QUOTE_MAP = {
  '0': '1423.03',
  '1': '1039.91',
  '2': '820.98',
  '3': '419',
};

// ===== HELPER: Get last day of a month =====
const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// ===== HELPER: Is a YYYY-MM-DD string in a prior month relative to today? =====
const isPriorMonth = (dateStr) => {
  if (!dateStr) return false;
  const [y, m] = dateStr.split('-').map(Number);
  const now = new Date();
  return y < now.getFullYear() || (y === now.getFullYear() && m - 1 < now.getMonth());
};


const NewPeriodical = () => {
  // --- User & role ---
  const { user } = useUser();
  const isIt = rolesAccess.it.includes(user?.userJob || '');
  const isAdmin = rolesAccess.admin.includes(user?.userJob || '');
  const isSupervisor = rolesAccess.supervisor.includes(user?.userJob || '');
  const isManagement = rolesAccess.management.includes(user?.userJob || '');

  // Only IT may set prior-month dates (protects the Airtable rollover
  // automation). Everyone else can only create current/forward periodicals.
  const canEditPriorDates = isIt;

  // Quote visibility: normal users never see the quote for Qantas Jet Base,
  // but it is still populated in the background and submitted.
  const canSeeQuote = isAdmin || isSupervisor || isManagement;

  const [formData, setFormData] = useState({
    period: '',
    site: '',
    datestart: '',
    dateend: '',
    cycletype2: 'Once-Off',
    quote: '0',
    cleaners: '1',
    rework: '0',
    chemical: '0',
    equipment: '0',
    incidental: '0',
    attend: '',
    rate: '28',
    days: '1',
    reworkpercent: '0',
    percentrate1: '60',
    worktype: 'TFN',
    workstatus2: 'Pending',
    planes: '0',
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sites, setSites] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showAddSite, setShowAddSite] = useState(false);

  const isQantasSite = formData.site === QANTAS_SITE;

  // Add-site icon + button only appear once a valid entry has been
  // selected from the autocomplete list.
  const siteSelected = !!formData.site && sites.includes(formData.site);


  useEffect(() => {
    const link1 = document.createElement('link');
    link1.rel = 'stylesheet';
    link1.href = cssUrl('newuseronboarding.css');
    document.head.appendChild(link1);

    const link2 = document.createElement('link');
    link2.rel = 'stylesheet';
    link2.href = cssUrl('filterTemplate.css');
    document.head.appendChild(link2);

    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
    };
  }, []);


  useEffect(() => {
    const loadSites = async () => {
      try {
        const data = await fetchPeriodicalSites();
        setSites(data.sites || []);
      } catch (err) {
        console.error('Failed to load sites:', err);
      }
    };
    loadSites();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const setValue = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => new Set([...prev, field]));
  };

  // ── Site select: only values that exist in the sites autocomplete list
  //    are accepted — typed-over/extra text is flagged and blocks submit.
  //    Selecting Qantas Jet Base locks the quote to the planes mapping;
  //    switching away resets planes to the default '0'. ──
  const handleSiteChange = (val) => {
    const isValidSite = !val || sites.includes(val);

    setFormData((prev) => {
      const next = { ...prev, site: val };
      if (val === QANTAS_SITE) {
        next.planes = prev.planes || '0';
        next.quote = PLANES_QUOTE_MAP[next.planes];
      } else {
        next.planes = '0';
      }
      return next;
    });
    setTouched((prev) => new Set([...prev, 'site']));
    setErrors((prev) => {
      const n = { ...prev };
      delete n.quote;
      if (isValidSite) {
        delete n.site;
      } else {
        n.site = 'Please select a site from the list — no extra text.';
      }
      return n;
    });
  };

  // ── New site created via AddNewSiteModal: append it to the autocomplete
  //    list and auto-select it. Done manually (not via handleSiteChange)
  //    because the `sites` closure wouldn't contain the new value yet. ──
  const handleSiteCreated = (newSite) => {
    setSites((prev) => (prev.includes(newSite) ? prev : [...prev, newSite]));
    setFormData((prev) => {
      const next = { ...prev, site: newSite };
      if (newSite === QANTAS_SITE) {
        next.planes = prev.planes || '0';
        next.quote = PLANES_QUOTE_MAP[next.planes];
      } else {
        next.planes = '0';
      }
      return next;
    });
    setTouched((prev) => new Set([...prev, 'site']));
    setErrors((prev) => { const n = { ...prev }; delete n.site; delete n.quote; return n; });
    setShowAddSite(false);
    setToast({ type: 'success', message: `Site "${newSite}" added and selected.` });
  };

  // ── Planes select: always populates the quote in the background for Qantas ──
  const handlePlanesChange = (val) => {
    setFormData((prev) => ({
      ...prev,
      planes: val,
      ...(prev.site === QANTAS_SITE ? { quote: PLANES_QUOTE_MAP[val] } : {}),
    }));
    setTouched((prev) => new Set([...prev, 'planes']));
  };


  const handleTextChange = (field, e) => {
    const val = e.target.value;
    setValue(field, val);
    if (!val.trim()) {
      setErrors((prev) => ({ ...prev, [field]: 'This field is required' }));
    } else {
      setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  const handleNumberChange = (field, e) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    setValue(field, val);
    if (val && isNaN(Number(val))) {
      setErrors((prev) => ({ ...prev, [field]: 'Must be a valid number' }));
    } else {
      setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  // ─────────────────────────────────────────────
  //  DATE HANDLERS — anchor-based (aligned with NewReplacement)
  // ─────────────────────────────────────────────
  //
  //  Rules:
  //  • Admin can pick any date (including prior months).
  //  • Non-admin: selecting a prior month → toast + clear BOTH dates.
  //  • START is the anchor. Changing start always resets end to match start;
  //    the user then extends end forward as needed.
  //  • END cannot go before start (toast + reject — user must change start
  //    to shift the range backward).
  //  • END cannot leave start's month (clamped to EOM with a toast).
  //
  const clearDates = () => {
    setFormData((prev) => ({ ...prev, datestart: '', dateend: '' }));
    setErrors((prev) => { const n = { ...prev }; delete n.datestart; delete n.dateend; return n; });
  };

  const handleStartDateChange = (val) => {
    if (!val) {
      setValue('datestart', '');
      setErrors((prev) => ({ ...prev, datestart: 'Date is required' }));
      return;
    }

    
    if (!canEditPriorDates && isPriorMonth(val)) {
      setToast({ type: 'error', message: 'Cannot create a periodical for a prior month' });
      clearDates();
      return;
    }

    // Start is the anchor — any change to start resets end to match start.
    // The user then extends end forward (within month) as needed.
    const endVal = val;

    setFormData((prev) => ({ ...prev, datestart: val, dateend: endVal }));
    setTouched((prev) => new Set([...prev, 'datestart', 'dateend']));
    setErrors((prev) => { const n = { ...prev }; delete n.datestart; delete n.dateend; return n; });
  };

  const handleEndDateChange = (val) => {
    if (!val) {
      setValue('dateend', '');
      setErrors((prev) => ({ ...prev, dateend: 'Date is required' }));
      return;
    }

    // Prior-month gate
    if (!canEditPriorDates && isPriorMonth(val)) {
      setToast({ type: 'error', message: 'Cannot create a periodical for a prior month' });
      clearDates();
      return;
    }

    let startVal = formData.datestart;
    let endVal = val;

    // First-set auto-match: no start yet → start mirrors to end.
    // After this, start becomes the anchor; further end changes follow anchor rules.
    if (!startVal) {
      startVal = val;
      setFormData((prev) => ({ ...prev, datestart: startVal, dateend: endVal }));
      setTouched((prev) => new Set([...prev, 'datestart', 'dateend']));
      setErrors((prev) => { const n = { ...prev }; delete n.datestart; delete n.dateend; return n; });
      return;
    }

    // ── Anchor-based rules: start is the anchor, end is constrained ──

    // Floor: end cannot go before start. Change start instead.
    if (endVal < startVal) {
      setToast({
        type: 'error',
        message: 'End date cannot be before start date. Change the start date to shift the range.',
      });
      return;
    }

    // Ceiling: end must stay in the same month as start.
    const [sy, sm] = startVal.split('-').map(Number);
    const [ey, em] = endVal.split('-').map(Number);
    if (sy !== ey || sm !== em) {
      const lastDay = getLastDayOfMonth(sy, sm - 1);
      const pad = (n) => String(n).padStart(2, '0');
      const eomStr = `${sy}-${pad(sm)}-${pad(lastDay)}`;
      setToast({
        type: 'error',
        message: `End date must stay within the same month. Clamped to ${lastDay}th.`,
      });
      endVal = eomStr;
    }

    setFormData((prev) => ({ ...prev, datestart: startVal, dateend: endVal }));
    setTouched((prev) => new Set([...prev, 'datestart', 'dateend']));
    setErrors((prev) => { const n = { ...prev }; delete n.datestart; delete n.dateend; return n; });
  };


  const isFormValid = () => {
    const required = ['period', 'site', 'datestart', 'dateend', 'quote', 'cleaners', 'attend', 'rate', 'days'];
    for (const field of required) {
      if (!formData[field] || !formData[field].toString().trim()) return false;
    }
    if (Object.keys(errors).length > 0) return false;

    // Site must exactly match an option from the autocomplete list —
    // typed-over values like "Qantas Jet Base extra" cannot be submitted.
    if (!sites.includes(formData.site)) return false;

    if (!canEditPriorDates && (isPriorMonth(formData.datestart) || isPriorMonth(formData.dateend))) return false;
    return true;
  };


  const handleSubmit = async () => {
    if (!isFormValid() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await addNewPeriodical(formData);
      setToast({ type: 'success', message: 'Periodical created successfully!' });
      setTimeout(() => {
        window.history.back();
      }, 2000);
    } catch (err) {
      console.error('Submit failed:', err);
      setToast({ type: 'error', message: 'Failed to create periodical. Please try again.' });
      setIsSubmitting(false);
    }
  };


  const inputClass = (field) =>
    `ob-input ${touched.has(field) && errors[field] ? 'ob-input-error' : ''}`;

  return (
    <div className="ob-page">
      {toast && (
        <div className={`ob-toast ${toast.type === 'success' ? 'ob-toast-success' : 'ob-toast-error'}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === 'success'
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            }
          </svg>
          {toast.message}
        </div>
      )}

      <div className="ob-container">

        <div className="ob-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
          <h1 className="ob-title">New Periodical</h1>
          <p className="ob-subtitle">Fill in all fields to create a new periodical</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {siteSelected && (
            <button
              type="button"
              onClick={() => setShowAddSite(true)}
              title="Add new site"
              aria-label="Add new site"
              style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
                color: '#cbd5e1', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
              </svg>
            </button>
          )}
          <HelpButton helpData={[
              { chip: 'periodicalnew', urlFilter: '/static/pdffilters/PeriodicalNew.pdf' }
            ]} />
        </div>

        </div>

        {pdfUrl && (
          <ContractViewer pdfUrl={pdfUrl} onClose={() => setPdfUrl(null)} />
        )}

        {/* Raised above the form (modal uses its own fixed backdrop, z-index 1000) */}
        <AddNewSiteModal
          isOpen={showAddSite}
          onClose={() => setShowAddSite(false)}
          onCreated={handleSiteCreated}
        />

        <div className="ob-form-card">
          <div className="ob-form-fields">


            <div className="ob-field">
              <label className="ob-label">
                Periodical <span className="ob-label-required">*</span>
              </label>
              <textarea
                className={inputClass('period')}
                value={formData.period}
                onChange={(e) => handleTextChange('period', e)}
                placeholder="Enter periodical name"
                style={{ height: '80px', resize: 'none' }}
              />

              {touched.has('period') && errors.period && (
                <div className="ob-error">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.period}
                </div>
              )}
            </div>


            {/* ── Add New Site — only visible once a valid entry has been
                   selected from the autocomplete ── */}
            {siteSelected && (
              <div className="ob-field" style={{ marginBottom: '-6px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddSite(true)}
                  className="ob-btn"
                  style={{
                    width: '100%', background: 'transparent',
                    border: '1px dashed rgba(255,255,255,0.25)', color: '#cbd5e1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                  </svg>
                  Add New Site
                </button>
              </div>
            )}


            <div className="ob-field">
              <CustomSelect
                label="Site"
                value={formData.site}
                onChange={handleSiteChange}
                options={sites}
              />
              {touched.has('site') && errors.site && (
                <div className="ob-error">{errors.site}</div>
              )}
            </div>


            {/* ── Qantas only: planes select drives the quote.
                   Shown to all users, including non-admin staff. ── */}
            {isQantasSite && (
              <div className="ob-field">
                <CustomSelect
                  label="Number of Planes"
                  value={formData.planes}
                  onChange={handlePlanesChange}
                  options={PLANES_OPTIONS}
                />
              </div>
            )}


            <div className="ob-field">
              <label className="ob-label">
                Start Date <span className="ob-label-required">*</span>
              </label>
              <input
                type="date"
                value={formData.datestart}
                onChange={(e) => handleStartDateChange(e.target.value)}
                className={inputClass('datestart')}
              />
            </div>


            <div className="ob-field">
              <label className="ob-label">
                End Date <span className="ob-label-required">*</span>
              </label>
              <input
                type="date"
                value={formData.dateend}
                onChange={(e) => handleEndDateChange(e.target.value)}
                className={inputClass('dateend')}
              />
            </div>


            <div className="ob-field">
              <CustomSelect
                label="Periodical Cycle"
                value={formData.cycletype2}
                onChange={(val) => setValue('cycletype2', val)}
                options={CYCLE_OPTIONS}
              />
            </div>


            {/* ── Quote:
                   • Qantas → read-only for everyone (driven by planes),
                     and hidden entirely for non-admin/supervisor/management users
                     (still populated + submitted in the background).
                   • All other sites → editable as before. ── */}
            {(!isQantasSite || canSeeQuote) && (
              <div className="ob-field">
                <label className="ob-label">
                  Quote <span className="ob-label-required">*</span>
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.quote}
                  onChange={(e) => handleNumberChange('quote', e)}
                  placeholder="0.00"
                  readOnly={isQantasSite}
                  className={inputClass('quote')}
                />
                {touched.has('quote') && errors.quote && (
                  <div className="ob-error">{errors.quote}</div>
                )}
              </div>
            )}


            <div className="ob-field">
              <label className="ob-label">
                Number of Cleaners <span className="ob-label-required">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.cleaners}
                onChange={(e) => handleNumberChange('cleaners', e)}
                placeholder="0"
                className={inputClass('cleaners')}
              />
              {touched.has('cleaners') && errors.cleaners && (
                <div className="ob-error">{errors.cleaners}</div>
              )}
            </div>


            <div className="ob-field">
              <label className="ob-label">Rework Value</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.rework}
                onChange={(e) => handleNumberChange('rework', e)}
                placeholder="0"
                className={inputClass('rework')}
              />
            </div>


            <div className="ob-field">
              <label className="ob-label">Chemicals</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.chemical}
                onChange={(e) => handleNumberChange('chemical', e)}
                placeholder="0"
                className={inputClass('chemical')}
              />
            </div>


            <div className="ob-field">
              <label className="ob-label">Equipment</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.equipment}
                onChange={(e) => handleNumberChange('equipment', e)}
                placeholder="0"
                className={inputClass('equipment')}
              />
            </div>


            <div className="ob-field">
              <label className="ob-label">Incidental</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.incidental}
                onChange={(e) => handleNumberChange('incidental', e)}
                placeholder="0"
                className={inputClass('incidental')}
              />
            </div>


            <div className="ob-field">
              <label className="ob-label">
                Cleaner Attending <span className="ob-label-required">*</span>
              </label>
              <input
                type="text"
                value={formData.attend}
                onChange={(e) => handleTextChange('attend', e)}
                placeholder="Enter cleaner name"
                className={inputClass('attend')}
              />
              {touched.has('attend') && errors.attend && (
                <div className="ob-error">{errors.attend}</div>
              )}
            </div>


            <div className="ob-field">
              <label className="ob-label">
                Rate <span className="ob-label-required">*</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.rate}
                onChange={(e) => handleNumberChange('rate', e)}
                placeholder="0.00"
                className={inputClass('rate')}
              />
              {touched.has('rate') && errors.rate && (
                <div className="ob-error">{errors.rate}</div>
              )}
            </div>


            <div className="ob-field">
              <label className="ob-label">
                Completion Days <span className="ob-label-required">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.days}
                onChange={(e) => handleNumberChange('days', e)}
                placeholder="0"
                className={inputClass('days')}
              />
              {touched.has('days') && errors.days && (
                <div className="ob-error">{errors.days}</div>
              )}
            </div>


            <div className="ob-field">
              <label className="ob-label">Rework Percent</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.reworkpercent}
                onChange={(e) => handleNumberChange('reworkpercent', e)}
                placeholder="0"
                className={inputClass('reworkpercent')}
              />
            </div>


            <div className="ob-field">
              <label className="ob-label">Percent Rate</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.percentrate1}
                onChange={(e) => handleNumberChange('percentrate1', e)}
                placeholder="0"
                className={inputClass('percentrate1')}
              />
            </div>


            <div className="ob-field">
              <CustomSelect
                label="Work Type"
                value={formData.worktype}
                onChange={(val) => setValue('worktype', val)}
                options={WORK_TYPE_OPTIONS}
              />
            </div>


            <div className="ob-field">
              <CustomSelect
                label="Work Status"
                value={formData.workstatus2}
                onChange={(val) => setValue('workstatus2', val)}
                options={WORK_STATUS_OPTIONS}
              />
            </div>


            <button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              className="ob-btn ob-btn-submit"
            >
              {isSubmitting ? (
                <span className="ob-spinner">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating…
                </span>
              ) : (
                'Create Periodical'
              )}
            </button>

          </div>
        </div>

        <p className="ob-footer">New Periodical</p>
      </div>
    </div>
  );
};

export default NewPeriodical;